// wiring.js — pure, unit-testable mapping between the live craftjs Image node props
// and the WL-011 ImageEditor's controlled `value`. WL-011b.
//
// The live Image node keeps the picture under `image: { src, title }` (with legacy
// flat `src`/`title` mirrors), stores `width` as a percent STRING ("100%"), and
// `isApplyMarginOnAllSides` for the margin toggle. This module normalizes those to
// the editor's flat number/boolean value, and back.
//
// STYLE props are mapped by nodePropsToValue/applyValueToProps. The image BYTES
// (upload / browse / recent / remove) are mapped by the WL-011c helpers at the
// bottom of this file (applyUploadedMediaToProps / applyPickedMediaToProps /
// removeImageFromProps), which the ImageEditorSettings adapter drives from the real
// S3 pipeline. applyValueToProps still preserves the existing image.src/title on a
// style-only edit (it only carries next.src/next.title through), never blanking a
// real picture on an unrelated control change.

// Width converters — kept in exact parity with utils/helpers.js
// sliderToPercentage / percentageToSlider (the legacy ImageSettings uses those). Inlined
// here rather than imported so this pure mapping module does NOT drag the utils/helpers
// barrel (which pulls segmentTools -> the whole CodeBlocks/Froala chain) into every
// consumer + the unit test.
const sliderToPercentage = (v) => `${v}%`;
const percentageToSlider = (p) => parseInt(p, 10);

// craftjs node props  ->  ImageEditor `value`
export function nodePropsToValue(props = {}) {
  const p = props || {};
  const img = p.image || {};
  const rawWidth = p.width;
  const width =
    rawWidth == null || rawWidth === ""
      ? 100
      : typeof rawWidth === "number"
      ? rawWidth
      : percentageToSlider(rawWidth) || 100;
  return {
    // The chosen SOURCE tab (Upload / Embed URL / Browse / Recent) is real editor
    // state and must round-trip through craft props. Hard-coding it here made
    // every tab click a no-op: ImageEditor calls patch({ source }) -> onChange ->
    // applyValueToProps, and the next render recomputed the constant, so the
    // Embed-URL field and the Browse/Recent pickers could never mount.
    source: p.source || "Upload",
    src: img.src || p.src || "",
    title: img.title || p.title || "",
    showImageTitle: !!p.showImageTitle,
    alignment: p.alignment || "left",
    showHyperlink: !!p.showHyperlink,
    hyperlink: p.hyperlink || "",
    width,
    borderRadius: p.borderRadius ?? 0,
    borderColor: p.borderColor ?? "#E4E7EC",
    borderWidth: p.borderWidth ?? 0,
    dropShadowColor: p.dropShadowColor ?? "",
    margin: { top: 0, right: 0, bottom: 0, left: 0, ...(p.margin || {}) },
    applyMarginAll: !!p.isApplyMarginOnAllSides,
    // IMAGE_CAPTION_OVERLAY (additive, flag-gated): the structured Text overlay
    // (undefined for an image with no caption overlay). Passed through verbatim so
    // the editor can show the current overlay; a plain image leaves this undefined.
    captionOverlay: p.captionOverlay,
  };
}

// ImageEditor `value`  ->  mutate a craftjs draft props object in place.
export function applyValueToProps(next = {}, props = {}) {
  if (!props.image) props.image = {};
  if (!props.margin) props.margin = {};

  // Preserve the real picture; the v2 editor only edits style + caption/alt/link.
  props.image = { ...props.image, src: next.src, title: next.title };
  props.src = next.src;
  props.title = next.title;
  // Persist the source tab so the selection survives the value<->props round-trip.
  props.source = next.source || "Upload";
  props.showImageTitle = !!next.showImageTitle;
  props.alignment = next.alignment;
  props.showHyperlink = !!next.showHyperlink;
  props.hyperlink = next.hyperlink;
  props.width = sliderToPercentage(next.width);
  props.borderRadius = next.borderRadius;
  // Image uses flat borderColor/borderWidth (no nested `border` object).
  props.borderColor = next.borderColor;
  props.borderWidth = next.borderWidth;
  props.dropShadowColor = next.dropShadowColor || null;
  props.margin = { ...props.margin, ...(next.margin || {}) };
  props.isApplyMarginOnAllSides = !!next.applyMarginAll;

  // IMAGE_CAPTION_OVERLAY (additive, flag-gated). Tri-state on `next.captionOverlay`:
  //   • undefined -> NOT provided (the flag-OFF editor never sets it) -> DO NOT
  //     touch the node shape, so a plain image stays byte-identical.
  //   • null / falsy -> explicit clear -> remove the prop.
  //   • object -> a structured Text overlay -> persist it verbatim.
  if (next.captionOverlay !== undefined) {
    if (next.captionOverlay) props.captionOverlay = next.captionOverlay;
    else delete props.captionOverlay;
  }
  return props;
}

// ---------------------------------------------------------------------------
// WL-011c: image SELECTION mappings (upload / browse / recent / remove).
// Pure mutators of a craftjs draft props object — mirror the legacy
// ImageSettings.jsx handleChange("image" | "imageRemove", …) semantics exactly,
// so the v2 editor writes the SAME node shape the legacy editor wrote.
// ---------------------------------------------------------------------------

// A completed S3 upload -> node props. `location` is the object_url returned by
// utils/fileUpload.handleFileUpload; title is the original filename.
export function applyUploadedMediaToProps(
  { id, location, title, ext } = {},
  props = {}
) {
  if (!props.image) props.image = {};
  props.image = {
    ...props.image,
    id,
    src: location,
    title,
    loading: false,
    type: ext,
  };
  props.src = location;
  props.title = title;
  props.originalSrc = location;
  props.isLoading = false;
  return props;
}

// A picked media item (Browse / Recent onConfirm) -> node props. Mirrors legacy
// handleChange("image", value): merges the partial into props.image and derives
// isLoading from an explicit `loading` key when present (the picker emits
// { loading:true } then the resolved item).
export function applyPickedMediaToProps(val = {}, props = {}) {
  if (!props.image) props.image = {};
  props.image = { ...props.image, ...val };
  if (Object.prototype.hasOwnProperty.call(val, "loading")) {
    props.isLoading = val.loading !== false;
  }
  if (val.src) {
    props.src = val.src;
    props.originalSrc = val.src;
    if (val.title != null) props.title = val.title;
  }
  return props;
}

// Remove the current image from the node (legacy imageRemove cleared props.image;
// v2 also clears the flat src/title so the editor value goes back to empty).
export function removeImageFromProps(props = {}) {
  props.image = {};
  props.src = "";
  props.title = "";
  props.isLoading = false;
  return props;
}

const wiring = {
  nodePropsToValue,
  applyValueToProps,
  applyUploadedMediaToProps,
  applyPickedMediaToProps,
  removeImageFromProps,
};
export default wiring;
